import { forwardRef } from "react";

import { faWarning } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { twJoin } from "tailwind-merge";

import { HIGHLIGHT_CLASSES } from "src/flowGraph/canvasSearch/CanvasSearchHighlightContext";
import { useHighlightedScrollIntoView } from "src/utils/useHighlightedScrollIntoview";

type TextAreaProps = Omit<
  React.ComponentPropsWithoutRef<"textarea">,
  "style" | "className"
> & {
  errored?: boolean;
  resizeable?: boolean;
  fullWidth?: boolean;
  fullHeight?: boolean;
  isSearchHighlighted?: boolean;
};

export const Textarea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      errored,
      resizeable = false,
      fullWidth,
      fullHeight,
      isSearchHighlighted,
      ...props
    },
    ref,
  ) => {
    const [outerRef] = useHighlightedScrollIntoView(isSearchHighlighted);

    return (
      <div
        ref={outerRef}
        className={twJoin(
          "relative",
          fullWidth && "w-full",
          fullHeight && "h-full",
        )}
      >
        <textarea
          ref={ref}
          className={twJoin(
            "rounded-lg border px-2.5 py-2 font-inter-normal-12px focus:outline-none",
            "decideScrollbar h-20 min-h-[80px] w-full",
            "placeholder:text-gray-500",
            props.disabled
              ? "bg-gray-50 text-gray-500"
              : "cursor-text bg-white text-gray-800",
            errored
              ? "border-red-600 pr-7.5 ring-2 ring-red-400/25"
              : "border-gray-200 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-500/25",
            !resizeable && "resize-none",
            fullWidth && "w-full",
            fullHeight && "h-full",
            isSearchHighlighted && HIGHLIGHT_CLASSES,
          )}
          {...props}
        />
        {errored && (
          <FontAwesomeIcon
            className="absolute top-3.5 right-4 font-inter-normal-12px text-red-600"
            icon={faWarning}
          />
        )}
      </div>
    );
  },
);
